import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import classes from "app/css/widgets/DFNumericTextBox.module.less";

const DFNumericTextBox = BaseWidget(kendo.ui.NumericTextBox).extend({
    MIN: -9007199254740992,
    MAX: 9007199254740992,
    options: {
        name: "DFNumericTextBox",
        min: -9007199254740992,
        max: 9007199254740992,
        initTooltip: false,
        /** Locale key for the units to display after the box. Key
         * should be for the plural version(for example, "days").
         * Locale manager supports a special feature that will check for "days.conditional.1" for the singular version.
         */
        units: "",

        /**
         * Non-localized units.
         */
        unitsLocalized: "",

        /**
         * Selector to where the prompt help text should be rendered
         */
        promptHelpText: "",

        // the min value for when to show the hint, i.e. if 0, the hint is only shown when the min option is 0 or more
        hintMin: 0
    },

    events: [
        "inputChange"
    ],

    init: function(element, options) {
        var el = $(element);
        options.spinners = false;
        options.min = el.attr("min") || options.min;
        options.max = el.attr("max") || options.max;

        if(options.decimals === 0) {
            options.restrictDecimals = true;
        }

        kendo.ui.NumericTextBox.fn.init.apply(this, arguments);
        this.wrapper.addClass(classes.DFNumericTextBoxWrap);
        this._showTooltip = $.proxy(this._showTooltip, this);
        this._hideTooltip = $.proxy(this._hideTooltip, this);
        this.element.on("focus", this._showTooltip);
        this.element.on("blur", this._hideTooltip);
        this.element.on("input.DFNumericTextBox", () => {
            const value = this._parse(this._oldText);
            this.trigger("inputChange", {value});
        });

        if (this.options.initTooltip) {
            this._showTooltip();
        }

        var input = this.wrapper.find(".k-formatted-value");
        var id = this.element.attr("id");
        input.attr("id", id + "_numeric");
        this.element.parents().find("label[for='" + id + "']").attr("for", id + "_numeric");

    },

    _update: function(value) {
        kendo.ui.NumericTextBox.fn._update.apply(this, arguments);
        if (this.options.units || this.options.unitsLocalized) {
            if (!this.units) {
                this.units = $("<span>").addClass("units").insertAfter(this.wrapper);
            }

            if (this.options.units) {
                this.units.text(locale.get(this.options.units, value));
            }

            if (this.options.unitsLocalized) {
                this.units.text(this.options.unitsLocalized);
            }
        }
    },

    /**
     * Override of the private kendo method
     * this is a temporary solution to the accessibility issue where a value changes automatically if it exceeds min or max
     * ideally kendo would fix their widget to avoid adjusting values
     */
    _adjust: function(value) {
        return value;
    },

    destroy: function() {
        this.element.off("focus", this._showTooltip);
        this.element.off("blur", this._hideTooltip);
        this.element.off(".DFNumericTextBox");
        delete this.tooltip;
        if (this.units) {
            this.units.remove();
            delete this.units;
        }

        kendo.ui.NumericTextBox.fn.destroy.apply(this, arguments);
    },

    _readonlyCache: false,
    _enableCache: true,

    readonly: function(readonly) {
        this.wrapper.toggleClass("readonly", !!readonly);
        this._readonlyCache = !!readonly;
        this.options.readonly = this._readonlyCache;
        this._editable({
            readonly: this._readonlyCache,
            disable: !this._enableCache
        });
        this._updateReadonly(this._readonlyCache);
    },

    enable: function(enable) {
        this._enableCache = !!enable;
        this.options.enable = this._enableCache;
        this._editable({
            readonly: this._readonlyCache,
            disable: !this._enableCache
        });
        this._updateReadonly(this._readonlyCache);
    },

    _updateReadonly: function(val) {
        if (val) {
            this._text.attr("tabindex", "-1");
            this._removeTooltip();
        } else if (this.element.attr("tabindex")) {
            this._text.attr("tabindex", this.element.attr("tabindex"));
        } else {
            this._text.removeAttr("tabindex");
        }
    },

    _buildTooltip: function() {
        var message = null;

        // Mantis 37429: NULL in either min or max impacts readability of the prompt... if we find NULL in
        // either min or max, use default values.
        if (this.options.min === null) {
            this.options.min = this.MIN;
        }

        if (this.options.max === null) {
            this.options.max = this.MAX;
        }

        if(this.options.min < this.options.hintMin) {
            return;
        }

        if (this.options.min > this.MIN && this.options.max < this.MAX) {
            message = locale.get("numerictextbox.range", this.options.min, this.options.max);
        } else if (this.options.min > this.MIN) {
            message = locale.get("numerictextbox.min", this.options.min);
        } else if (this.options.max < this.MAX) {
            message = locale.get("numerictextbox.max", this.options.max);
        }

        if (this.tooltip && message) {
            this.tooltip.text(message);
        } else if (message) {
            var wrap = this.wrapper;
            wrap = wrap.next().hasClass("units") ? wrap.next() : wrap;
            this.tooltip = $("<span>")
                .addClass("promptHelpText")
                .text(message)
                .css("visibility", "hidden");
            var prompt = this.options.promptHelpText ? $(this.options.promptHelpText) : [];
            if (prompt.length) {
                prompt.append(this.tooltip);
            } else {
                this.tooltip.insertAfter(wrap);
            }
        }
    },

    _showTooltip: function() {
        if (this.options.promptHelpText !== false && !this.element.attr("readonly")) {
            this._buildTooltip();
            if (this.tooltip) {
                this.tooltip.css("visibility", "visible");
            }

            // select all current text
            var target = this.element;
            setTimeout(function() {
                target.select();
            });
        }
    },

    _hideTooltip: function() {
        if (this.tooltip) {
            this.tooltip.css("visibility", "hidden");
        }
    },

    _removeTooltip: function() {
        if (this.tooltip) {
            this.tooltip.remove();
            delete this.tooltip;
        }
    }
});
kendo.ui.plugin(DFNumericTextBox);
export default DFNumericTextBox;
